import type { ConnectorType } from '@/lib/types'

export interface ConnectorPrompt {
  text: string
  /** Short label for the chip (falls back to text if not provided) */
  label?: string
}

/**
 * Suggested prompts per connector type.
 * Each connector surfaces 2-3 prompts that demonstrate what users can do with it.
 */
const connectorPrompts: Record<ConnectorType, ConnectorPrompt[]> = {
  postgres: [
    {
      label: 'Pull latest rows',
      text: 'Pull the latest 100 rows from my database into this sheet',
    },
    {
      label: 'Summarize tables',
      text: 'Show a summary of all tables in my database',
    },
    {
      label: 'Chart from DB',
      text: 'Query my database for revenue by month and create a chart',
    },
  ],
  mysql: [
    {
      label: 'Pull latest rows',
      text: 'Pull the latest 100 rows from my database into this sheet',
    },
    {
      label: 'Summarize tables',
      text: 'Show a summary of all tables in my database',
    },
  ],
  gmail: [
    { label: 'Summarize inbox', text: 'Summarize my unread emails from today' },
    {
      label: 'Find invoices',
      text: 'Find all invoice emails from this month and list them in a table',
    },
    {
      label: 'Draft replies',
      text: 'Show my unanswered emails and draft replies for each one',
    },
  ],
  google_calendar: [
    {
      label: "This week's meetings",
      text: 'Show my meetings for this week in a table',
    },
    {
      label: 'Find conflicts',
      text: 'Check my calendar for scheduling conflicts this week',
    },
    {
      label: 'Block focus time',
      text: 'Find open slots and create 2-hour focus time blocks this week',
    },
  ],
  google_drive: [
    {
      label: 'Recent files',
      text: 'List my 20 most recently modified files from Google Drive',
    },
    {
      label: 'Find spreadsheets',
      text: 'Search my Google Drive for all spreadsheets',
    },
  ],
  google_docs: [
    {
      label: 'Create report',
      text: 'Create a Google Doc report summarizing the data in this sheet',
    },
    {
      label: 'Import doc',
      text: 'Pull the contents of my most recent Google Doc into this sheet',
    },
  ],
  google_contacts: [
    {
      label: 'Import contacts',
      text: 'Import my Google Contacts into this sheet',
    },
    {
      label: 'Search contacts',
      text: 'Search my contacts for people at a specific company',
    },
  ],
  slack: [
    {
      label: 'Share to Slack',
      text: 'Send a summary of this sheet to my Slack channel',
    },
    {
      label: 'Notify team',
      text: 'Post a message to Slack with the latest numbers from this sheet',
    },
  ],
  linkedin: [
    {
      label: 'Draft a post',
      text: "Draft a LinkedIn post about this week's key metrics from my sheet",
    },
    {
      label: 'Company update',
      text: 'Write a LinkedIn company update based on the data in this sheet',
    },
  ],
  resend: [
    {
      label: 'Email report',
      text: 'Email a formatted summary of this sheet as a report',
    },
    {
      label: 'Bulk email',
      text: 'Send a personalized email to each contact listed in this sheet',
    },
  ],
}

/** Default prompts when no connectors are configured */
const defaultPrompts: ConnectorPrompt[] = [
  {
    label: 'Format as table',
    text: 'Format my data as a clean table with headers',
  },
  {
    label: 'Create a chart',
    text: 'Create a chart from the data in this sheet',
  },
  {
    label: 'Write formulas',
    text: 'Add formulas to calculate totals and averages for my data',
  },
  {
    label: 'Analyze data',
    text: 'Analyze the data in this sheet and give me key insights',
  },
]

export type ConnectorInfo = {
  type: ConnectorType
  name: string
}

/**
 * Pick the best suggestions based on active connectors.
 * Returns up to `max` prompts, mixing connector-specific and default prompts.
 */
export function getSuggestedPrompts(
  connectors: ConnectorInfo[],
  max = 6
): ConnectorPrompt[] {
  if (connectors.length === 0) {
    return defaultPrompts.slice(0, max)
  }

  // Deduplicate connector types (user might have 2 postgres connections)
  const uniqueTypes = [...new Set(connectors.map((c) => c.type))]

  // Collect prompts: take first 2 from each connector type, round-robin
  const result: ConnectorPrompt[] = []
  const seen = new Set<string>()

  // Round 1: first prompt from each connector type
  for (const type of uniqueTypes) {
    const prompts = connectorPrompts[type]
    if (prompts?.[0] && !seen.has(prompts[0].text)) {
      result.push(prompts[0])
      seen.add(prompts[0].text)
    }
    if (result.length >= max) return result.slice(0, max)
  }

  // Round 2: second prompt from each connector type
  for (const type of uniqueTypes) {
    const prompts = connectorPrompts[type]
    if (prompts?.[1] && !seen.has(prompts[1].text)) {
      result.push(prompts[1])
      seen.add(prompts[1].text)
    }
    if (result.length >= max) return result.slice(0, max)
  }

  // Fill remaining slots with defaults
  for (const prompt of defaultPrompts) {
    if (!seen.has(prompt.text)) {
      result.push(prompt)
      seen.add(prompt.text)
    }
    if (result.length >= max) break
  }

  return result.slice(0, max)
}
